/**
 * Workflow Search Hub Component
 * 
 * A comprehensive search interface for all 15 workflow types
 * Features:
 * - Global search across all workflow types
 * - Tabulator-powered results table with filtering
 * - Lower half menu navigation
 * - Real-time search with debouncing
 * - Type-specific search filters
 */

import {
    ActionIcon,
    Affix,
    Badge,
    Box,
    Button,
    Card,
    Chip,
    Container,
    Drawer,
    Grid,
    Group,
    Modal,
    Paper,
    SegmentedControl,
    Select,
    Stack,
    Tabs,
    Text,
    TextInput,
    Title,
    Tooltip,
    Transition
} from '@mantine/core';
import { useDebouncedValue, useDisclosure, useHotkeys } from '@mantine/hooks';
import {
    IconAdjustments,
    IconDatabase,
    IconFilter,
    IconLayoutGrid,
    IconList,
    IconSearch,
    IconX
} from '@tabler/icons-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TabulatorFull } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_midnight.min.css';
import { WorkflowMetadata } from './index';

// Import from our new data layer
import { SearchResult, useWorkflowSearch } from '../data';

// Import luxon for datetime formatting - make it available globally for Tabulator
try {
    const luxon = require('luxon');
    if (typeof window !== 'undefined') {
        (window as any).luxon = luxon;
    }
} catch {
    // Luxon not available, will use fallback formatter
}

// SearchResult now imported from data layer

interface WorkflowSearchHubProps {
    onItemSelect?: (item: SearchResult) => void;
    initialQuery?: string;
}

export const WorkflowSearchHub: React.FC<WorkflowSearchHubProps> = ({
    onItemSelect,
    initialQuery = ''
}) => {
    const [searchQuery, setSearchQuery] = useState(initialQuery);
    const [debouncedQuery] = useDebouncedValue(searchQuery, 300);
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
    const [sortBy, setSortBy] = useState('relevance');
    const [menuOpened, { open: openMenu, close: closeMenu, toggle: toggleMenu }] = useDisclosure(false);
    const [filterOpened, { toggle: toggleFilter }] = useDisclosure(false);
    const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);

    const tableRef = useRef<HTMLDivElement>(null);
    const tableInstance = useRef<InstanceType<typeof TabulatorFull> | null>(null);

    // Use our new workflow search hook
    const {
        results: searchResults,
        loading: searchLoading,
        error: searchError
    } = useWorkflowSearch(debouncedQuery);

    // Initialize Tabulator
    useEffect(() => {
        if (!tableRef.current || viewMode !== 'table') return undefined;

        tableInstance.current = new TabulatorFull(tableRef.current, {
            data: filteredResults,
            height: 400,
            layout: 'fitColumns',
            responsiveLayout: 'collapse',
            pagination: true,
            paginationSize: 10,
            movableColumns: true,
            resizableRows: true,
            columns: [
                {
                    title: 'Type',
                    field: 'type',
                    width: 150,
                    formatter: (cell: any) => {
                        const type = cell.getValue() as string;
                        const metadata = WorkflowMetadata[type as keyof typeof WorkflowMetadata];
                        return `<span style="font-size: 18px">${metadata?.icon || '📄'}</span> ${type}`;
                    }
                },
                {
                    title: 'Title',
                    field: 'title',
                    formatter: 'plaintext',
                    sorter: 'string'
                },
                {
                    title: 'Description',
                    field: 'description',
                    formatter: 'textarea',
                    width: 300
                },
                {
                    title: 'Status',
                    field: 'status',
                    formatter: (cell: any) => {
                        const status = cell.getValue() as string;
                        const colors: Record<string, string> = {
                            active: 'green',
                            pending: 'orange',
                            completed: 'blue',
                            failed: 'red'
                        };
                        return `<span style="
                            background: ${colors[status] || 'gray'};
                            color: white;
                            padding: 2px 8px;
                            border-radius: 4px;
                            font-size: 12px;
                        ">${status}</span>`;
                    },
                    width: 100
                },
                {
                    title: 'Tags',
                    field: 'tags',
                    formatter: (cell: any) => {
                        const tags = cell.getValue() as string[];
                        return tags.map(tag =>
                            `<span style="
                                background: #e9ecef;
                                padding: 2px 6px;
                                border-radius: 3px;
                                margin-right: 4px;
                                font-size: 11px;
                            ">${tag}</span>`
                        ).join('');
                    }
                },
                {
                    title: 'Modified',
                    field: 'modified',
                    formatter: (window as any).luxon ? 'datetime' : function (cell: any) {
                        const date = cell.getValue();
                        if (!date) return '';
                        const d = new Date(date);
                        return d.toLocaleDateString('en-US');
                    },
                    formatterParams: (window as any).luxon ? {
                        outputFormat: 'MM/DD/YYYY'
                    } : undefined,
                    width: 120
                },
                {
                    title: 'Score',
                    field: 'score',
                    formatter: 'progress',
                    width: 100
                }
            ],
            rowClick: (e: Event, row: any) => {
                const data = row.getData() as SearchResult;
                setSelectedResult(data);
                onItemSelect?.(data);
            }
        });

        return () => {
            tableInstance.current?.destroy();
        };
    }, [viewMode, onItemSelect]);

    // Filter and sort results based on user preferences
    const filteredResults = useMemo(() => {
        let filtered = [...searchResults];

        // Filter by selected types
        if (selectedTypes.length > 0) {
            filtered = filtered.filter(item => selectedTypes.includes(item.type));
        }

        // Sort results
        if (sortBy === 'relevance') {
            filtered.sort((a, b) => b.score - a.score);
        } else if (sortBy === 'recent') {
            filtered.sort((a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime());
        } else if (sortBy === 'alphabetical') {
            filtered.sort((a, b) => a.title.localeCompare(b.title));
        }

        return filtered as any;
    }, [searchResults, selectedTypes, sortBy]);

    // Keyboard shortcuts
    useHotkeys([
        ['mod+k', () => document.getElementById('workflow-search')?.focus()],
        ['escape', () => closeMenu()],
        ['mod+f', () => toggleFilter()]
    ]);

    return (
        <Container size="xl" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Top Search Section */}
            <Paper shadow="sm" p="md" style={{ flex: '0 0 auto' }}>
                <Group justify="space-between" mb="md">
                    <Title order={2}>Workflow Search Hub</Title>
                    <Group>
                        <SegmentedControl
                            value={viewMode}
                            onChange={(v: string) => setViewMode(v as 'grid' | 'table')}
                            data={[
                                { label: 'Table', value: 'table' },
                                { label: 'Grid', value: 'grid' }
                            ]}
                        />
                        <Tooltip label="Filter Options">
                            <ActionIcon
                                variant={filterOpened ? 'filled' : 'default'}
                                onClick={toggleFilter}
                            >
                                <IconAdjustments size={18} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                </Group>

                <Grid>
                    <Grid.Col span={12}>
                        <TextInput
                            id="workflow-search"
                            placeholder="Search across all workflow types..."
                            leftSection={<IconSearch size={20} />}
                            rightSection={
                                searchQuery && (
                                    <ActionIcon
                                        size="sm"
                                        variant="subtle"
                                        onClick={() => setSearchQuery('')}
                                    >
                                        <IconX size={16} />
                                    </ActionIcon>
                                )
                            }
                            value={searchQuery}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.currentTarget.value)}
                            size="lg"
                        />
                    </Grid.Col>
                </Grid>

                {/* Filter Panel */}
                <Transition mounted={filterOpened} transition="slide-down" duration={200}>
                    {(styles: React.CSSProperties) => (
                        <Paper style={styles} mt="md" p="md" withBorder>
                            <Grid>
                                <Grid.Col span={6}>
                                    <Text size="sm" fw={500} mb="xs">Workflow Types</Text>
                                    <Chip.Group multiple value={selectedTypes} onChange={setSelectedTypes}>
                                        <Group gap="xs">
                                            {Object.keys(WorkflowMetadata).map(type => (
                                                <Chip key={type} value={type} size="xs">
                                                    {type}
                                                </Chip>
                                            ))}
                                        </Group>
                                    </Chip.Group>
                                </Grid.Col>
                                <Grid.Col span={3}>
                                    <Select
                                        label="Sort By"
                                        value={sortBy}
                                        onChange={(v: string | null) => setSortBy(v || 'relevance')}
                                        data={[
                                            { value: 'relevance', label: 'Relevance' },
                                            { value: 'recent', label: 'Most Recent' },
                                            { value: 'alphabetical', label: 'Alphabetical' }
                                        ]}
                                    />
                                </Grid.Col>
                                <Grid.Col span={3}>
                                    <Text size="sm" fw={500} mb="xs">Quick Actions</Text>
                                    <Group gap="xs">
                                        <Button
                                            size="xs"
                                            variant="light"
                                            onClick={() => setSelectedTypes([])}
                                        >
                                            Clear Filters
                                        </Button>
                                        <Button
                                            size="xs"
                                            variant="light"
                                            onClick={() => setSearchQuery('')}
                                        >
                                            Clear Search
                                        </Button>
                                    </Group>
                                </Grid.Col>
                            </Grid>
                        </Paper>
                    )}
                </Transition>

                {/* Results Count */}
                <Group justify="space-between" mt="md">
                    <Group>
                        <Text size="sm" c="dimmed">
                            Found {filteredResults.length} results
                            {debouncedQuery && ` for "${debouncedQuery}"`}
                        </Text>
                        {searchLoading && (
                            <Badge color="blue" variant="light">
                                Searching...
                            </Badge>
                        )}
                        {searchError && (
                            <Badge color="red" variant="light">
                                Error: {searchError.message}
                            </Badge>
                        )}
                    </Group>
                    <Group gap="xs">
                        {selectedTypes.map(type => (
                            <Badge
                                key={type}
                                variant="filled"
                                rightSection={
                                    <ActionIcon
                                        size="xs"
                                        variant="transparent"
                                        onClick={() => setSelectedTypes(prev =>
                                            prev.filter(t => t !== type)
                                        )}
                                    >
                                        <IconX size={10} />
                                    </ActionIcon>
                                }
                            >
                                {type}
                            </Badge>
                        ))}
                    </Group>
                </Group>
            </Paper>

            {/* Main Content Area */}
            <Box style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
                {viewMode === 'table' ? (
                    <div ref={tableRef} />
                ) : (
                    <Grid>
                        {filteredResults.map((result: SearchResult) => {
                            const metadata = WorkflowMetadata[result.type as keyof typeof WorkflowMetadata];
                            return (
                                <Grid.Col key={result.id} span={4}>
                                    <Card
                                        withBorder
                                        shadow="sm"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            setSelectedResult(result);
                                            onItemSelect?.(result);
                                        }}
                                    >
                                        <Group justify="space-between" mb="xs">
                                            <Text size="xl">{metadata?.icon}</Text>
                                            <Badge color={metadata?.color as any}>
                                                {result.type}
                                            </Badge>
                                        </Group>
                                        <Title order={5} mb="xs">{result.title}</Title>
                                        <Text size="sm" c="dimmed" lineClamp={2}>
                                            {result.description}
                                        </Text>
                                        <Group gap={4} mt="xs">
                                            {result.tags.slice(0, 3).map((tag: string, idx: number) => (
                                                <Badge key={idx} size="xs" variant="dot">
                                                    {tag}
                                                </Badge>
                                            ))}
                                        </Group>
                                    </Card>
                                </Grid.Col>
                            );
                        })}
                    </Grid>
                )}
            </Box>

            {/* Bottom Menu Drawer */}
            <Affix position={{ bottom: 20, right: 20 }}>
                <Button
                    leftSection={<IconLayoutGrid size={20} />}
                    onClick={toggleMenu}
                    size="lg"
                    radius="xl"
                    variant="filled"
                >
                    Quick Menu
                </Button>
            </Affix>

            <Drawer
                opened={menuOpened}
                onClose={closeMenu}
                position="bottom"
                size="50%"
                title="Workflow Navigation Menu"
                styles={{
                    body: { height: '100%' }
                }}
            >
                <Tabs defaultValue="types">
                    <Tabs.List>
                        <Tabs.Tab value="types" leftSection={<IconDatabase size={16} />}>
                            Workflow Types
                        </Tabs.Tab>
                        <Tabs.Tab value="recent" leftSection={<IconList size={16} />}>
                            Recent Searches
                        </Tabs.Tab>
                        <Tabs.Tab value="filters" leftSection={<IconFilter size={16} />}>
                            Saved Filters
                        </Tabs.Tab>
                        <Tabs.Tab value="advanced" leftSection={<IconAdjustments size={16} />}>
                            Advanced Search
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="types" pt="md">
                        <Grid>
                            {Object.entries(WorkflowMetadata).map(([type, metadata]) => (
                                <Grid.Col key={type} span={3}>
                                    <Button
                                        fullWidth
                                        variant="light"
                                        leftSection={<Text size="xl">{metadata.icon}</Text>}
                                        onClick={() => {
                                            setSelectedTypes([type]);
                                            closeMenu();
                                        }}
                                        styles={{
                                            root: { height: 'auto', padding: '12px' },
                                            inner: { justifyContent: 'flex-start' },
                                            label: { textAlign: 'left' }
                                        }}
                                    >
                                        <Stack gap={2}>
                                            <Text fw={500}>{metadata.title}</Text>
                                            <Text size="xs" c="dimmed">
                                                {metadata.features.length} features
                                            </Text>
                                        </Stack>
                                    </Button>
                                </Grid.Col>
                            ))}
                        </Grid>
                    </Tabs.Panel>

                    <Tabs.Panel value="recent" pt="md">
                        <Stack>
                            <Text c="dimmed">Recent search history will appear here</Text>
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="filters" pt="md">
                        <Stack>
                            <Text c="dimmed">Saved filter sets will appear here</Text>
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="advanced" pt="md">
                        <Grid>
                            <Grid.Col span={6}>
                                <TextInput
                                    label="Contains All Words"
                                    placeholder="workflow automation process"
                                />
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <TextInput
                                    label="Exact Phrase"
                                    placeholder="state machine"
                                />
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <TextInput
                                    label="Exclude Words"
                                    placeholder="draft archived"
                                />
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <Select
                                    label="Date Range"
                                    data={[
                                        'Last 24 hours',
                                        'Last 7 days',
                                        'Last 30 days',
                                        'Last year',
                                        'All time'
                                    ]}
                                />
                            </Grid.Col>
                            <Grid.Col span={12}>
                                <Group justify="flex-end">
                                    <Button variant="default" onClick={closeMenu}>
                                        Cancel
                                    </Button>
                                    <Button onClick={closeMenu}>
                                        Apply Advanced Search
                                    </Button>
                                </Group>
                            </Grid.Col>
                        </Grid>
                    </Tabs.Panel>
                </Tabs>
            </Drawer>

            {/* Selected Item Modal */}
            <Modal
                opened={!!selectedResult}
                onClose={() => setSelectedResult(null)}
                title={selectedResult?.title}
                size="lg"
            >
                {selectedResult && (
                    <Stack>
                        <Group justify="space-between">
                            <Badge size="lg">
                                {selectedResult.type}
                            </Badge>
                            <Badge color={
                                selectedResult.status === 'active' ? 'green' :
                                    selectedResult.status === 'pending' ? 'orange' :
                                        selectedResult.status === 'completed' ? 'blue' : 'red'
                            }>
                                {selectedResult.status}
                            </Badge>
                        </Group>
                        <Text>{selectedResult.description}</Text>
                        <Group gap="xs">
                            {selectedResult.tags.map((tag, idx) => (
                                <Badge key={idx} variant="outline">
                                    {tag}
                                </Badge>
                            ))}
                        </Group>
                        <Grid>
                            <Grid.Col span={6}>
                                <Text size="sm" c="dimmed">Created</Text>
                                <Text>{new Date(selectedResult.created).toLocaleDateString()}</Text>
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <Text size="sm" c="dimmed">Modified</Text>
                                <Text>{new Date(selectedResult.modified).toLocaleDateString()}</Text>
                            </Grid.Col>
                        </Grid>
                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => setSelectedResult(null)}>
                                Close
                            </Button>
                            <Button>
                                Open in Editor
                            </Button>
                        </Group>
                    </Stack>
                )}
            </Modal>
        </Container>
    );
};

export default WorkflowSearchHub;
